import i18n from '@/utils/i18n';
import SlidersIcon from '@assets/icons/sliders.svg';
import { Analytics } from '@/utils/analytics';
import { BadgeAbsoluteWithLabel } from '@/components/badge-absolute-with-label';
import { Button } from '@/components/button';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { HorizontalBar } from '@/components/dsr-analytics/horizontal-bar';
import { MultipleOptionsActionSheet } from '@/components/multiple-options-action-sheet';
import { PriceTrendsDisplayType } from '@/enums';
import { PriceTrendsGraph } from '@/components/dsr-analytics/price-trends-graph';
import { SalesRepTileWithUpperRightComponent } from './sales-rep-tile-with-upper-right-component';
import { Text, View } from 'react-native';
import { Toggle } from '@usepepper/ui/atoms/toggle';
import { useCustomerGroupListQuery } from '@/hooks/use-customer-group-list-query';
import {
  useGetDynamicConfigValueForChat,
  useSupplierDynamicConfigFlag,
} from '@/hooks/use-dynamic-config-value';
import { useLayoutContext } from '@/contexts/layout-context';
import { useMemo, useRef, useState } from 'react';
import { useUserContext } from '@/contexts/user-context';

const MENU_ICON_HITSLOP = {
  bottom: 8,
  left: 8,
  right: 8,
  top: 8,
};

interface Props {
  chatUUID: string;
  variantPackUUID: string;
}

export const PriceTrendsSalesRepTile: React.FC<Props> = ({
  chatUUID,
  variantPackUUID,
}) => {
  const buttonRef = useRef<View>(null);

  const [
    showCustomerGroupsFilterActionSheet,
    setShowCustomerGroupsFilterActionSheet,
  ] = useState(false);

  const { isGuestMode, restaurantUUIDToRelationshipMap, chatRestaurantMap } =
    useUserContext();
  const restaurant =
    restaurantUUIDToRelationshipMap[chatRestaurantMap[chatUUID]];

  const { getDynamicConfigValueForChat } = useGetDynamicConfigValueForChat();
  const { data: customerGroupsData, loading } = useCustomerGroupListQuery(
    restaurant.supplierUUID,
  );
  const priceTrendsConfig = getDynamicConfigValueForChat(
    chatUUID,
    'price_trends_config',
  );
  const showGrossProfit = !useSupplierDynamicConfigFlag(
    'sales_rep_config',
    'disable_gross_profit_display',
  );

  const defaultDisplayType = PriceTrendsDisplayType.Price;
  const { isSmallLayout } = useLayoutContext();
  const [selectedDisplayType, setSelectedDisplayType] =
    useState<PriceTrendsDisplayType>(defaultDisplayType);
  const [appliedDisplayType, setAppliedDisplayType] =
    useState<PriceTrendsDisplayType>(defaultDisplayType);
  const [styles] = useStyles();

  const [selectedRestaurantGroups, setSelectedRestaurantGroups] = useState<
    Set<string>
  >(new Set<string>());

  const supplierHasCustomerGroups = useMemo(
    () => customerGroupsData && customerGroupsData.restaurant_groups.length > 0,
    [customerGroupsData],
  );

  const selectedRestaurantGroupsCount = useMemo(
    () => Array.from(selectedRestaurantGroups).length,
    [selectedRestaurantGroups],
  );

  if (!priceTrendsConfig || isGuestMode) {
    return null;
  }

  const showCustomerGroupsFilter =
    priceTrendsConfig?.customer_group_filter_enabled &&
    supplierHasCustomerGroups;

  const showGrossProfitPercentageToggle =
    priceTrendsConfig?.gross_profit_percentage_toggle_enabled &&
    showGrossProfit;

  const upperRightComponent = () => {
    if (loading) {
      return null;
    }

    if (showCustomerGroupsFilter) {
      return (
        <>
          <Button
            accessibilityLabel={i18n.t('customer_groups.accessibility_label')}
            hitSlop={MENU_ICON_HITSLOP}
            hoverStyle={styles.buttonHoverStyle}
            Icon={SlidersIcon}
            onPress={() => setShowCustomerGroupsFilterActionSheet(true)}
            ref={buttonRef}
            size="small"
            style={styles.menuButton}
            trackingData={{
              name: 'price_trends.customer_groups_filter',
            }}
            type="tertiary"
          />
          {selectedRestaurantGroupsCount > 0 ? (
            <BadgeAbsoluteWithLabel
              badgeStyle={styles.badge}
              bodyContent={
                <Text
                  accessibilityLabel={i18n.t(
                    'customer_groups.badge.accessibility_label',
                    {
                      count: selectedRestaurantGroupsCount,
                    },
                  )}
                  style={styles.badgeText}
                >
                  {selectedRestaurantGroupsCount}
                </Text>
              }
            />
          ) : null}
        </>
      );
    } else if (showGrossProfitPercentageToggle) {
      return (
        <View style={styles.grossProfitToggleContainer}>
          <Toggle
            onPress={setAppliedDisplayType}
            selectedTab={appliedDisplayType}
            tabs={[
              {
                label: i18n.t('price_trends.price'),
                value: PriceTrendsDisplayType.Price,
              },
              {
                label: i18n.t('price_trends.gross_profit'),
                value: PriceTrendsDisplayType.GP,
              },
            ]}
          />
        </View>
      );
    } else {
      return null;
    }
  };

  return (
    <SalesRepTileWithUpperRightComponent
      title={i18n.t('price_distribution')}
      upperRightComponent={upperRightComponent()}
    >
      <PriceTrendsGraph
        displayMode={appliedDisplayType}
        restaurantGroupUUIDs={Array.from(selectedRestaurantGroups)}
        supplierUUID={restaurant.supplierUUID}
        variantPackUUID={variantPackUUID}
      />
      {showCustomerGroupsFilterActionSheet && customerGroupsData ? (
        <MultipleOptionsActionSheet
          buttonRef={buttonRef}
          headerElement={
            showGrossProfitPercentageToggle ? (
              <View>
                <Text
                  style={[
                    isSmallLayout ? TYPOGRAPHY.h2 : TYPOGRAPHY.medium,
                    FONTS.primarySemiBold,
                    styles.title,
                  ]}
                >
                  {i18n.t('price_trends.show_average')}
                </Text>
                <View
                  style={[
                    isSmallLayout
                      ? styles.displaySelectorContainer
                      : styles.displaySelectorContainerLarge,
                  ]}
                >
                  <Toggle
                    onPress={setSelectedDisplayType}
                    selectedTab={selectedDisplayType}
                    tabs={[
                      {
                        label: i18n.t('price_trends.price'),
                        value: PriceTrendsDisplayType.Price,
                      },
                      {
                        label: i18n.t('price_trends.gross_profit'),
                        value: PriceTrendsDisplayType.GP,
                      },
                    ]}
                  />
                </View>
                <HorizontalBar style={styles.horizontalBar} />
              </View>
            ) : null
          }
          initialSelectedOptions={selectedRestaurantGroups}
          onApplyChanges={(newSelectedRestaurantGroups) => {
            setSelectedRestaurantGroups(newSelectedRestaurantGroups);
            setAppliedDisplayType(selectedDisplayType);
            Analytics.track(
              'price_trends.customer_groups_filter.apply_changes',
              {
                restaurantGroupUUIDs: Array.from(selectedRestaurantGroups),
                restaurantUUID: restaurant.uuid,
                supplierUUID: restaurant.supplierUUID,
                variantPackUUID,
              },
            );
          }}
          onClose={() => setShowCustomerGroupsFilterActionSheet(false)}
          options={customerGroupsData!.restaurant_groups.map((group) => ({
            label: group.name,
            value: group.uuid,
          }))}
          title={i18n.t('customer_groups')}
        />
      ) : null}
    </SalesRepTileWithUpperRightComponent>
  );
};

const useStyles = createStyles((theme) => ({
  badge: {
    backgroundColor: theme.sk.color.bg.dark.default,
    borderRadius: 24,
    height: 16,
    minWidth: 16,
    position: 'absolute',
    right: -7,
    width: 'auto',
  },
  badgeText: {
    alignItems: 'center',
    color: theme.colors.white,
    display: 'flex',
    fontSize: theme.fontSizes.xsmall,
    fontWeight: 600,
    height: 16,
    justifyContent: 'center',
    marginHorizontal: 3,
    textAlign: 'center',
  },
  buttonHoverStyle: {
    opacity: 0.8,
  },
  displaySelectorContainer: {
    paddingBottom: 24,
    paddingHorizontal: 16,
  },
  displaySelectorContainerLarge: {
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  grossProfitToggleContainer: {
    height: 32,
    width: 88,
  },
  horizontalBar: {
    backgroundColor: theme.colors.background,
    height: 1,
    marginBottom: 16,
    marginHorizontal: 16,
  },
  menuButton: {
    alignSelf: 'flex-end',
    backgroundColor: theme.colors.white,
    borderRadius: 22,
    height: 32,
    width: 32,
  },
  title: {
    ...FONTS.primarySemiBold,
    paddingBottom: 16,
    paddingLeft: 24,
  },
}));
